import React, { useMemo } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import type { TemplateType } from '@/services/provisioningProfileService';
import { provisioningTemplateTypeItems } from './provisioningTemplateTypeMeta';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface ProvisioningTemplateTypeIconBarProps {
  onSelectTemplateType: (type: TemplateType) => void;
  onAddRoles: () => void;
  disabled?: boolean;
}

const btnClass =
  'flex flex-col items-center justify-center gap-1 min-w-[4.25rem] max-w-[5rem] px-2 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:border-[var(--color-info)] hover:bg-[var(--color-info)]/5 disabled:opacity-50 disabled:pointer-events-none transition-colors shrink-0';

export const ProvisioningTemplateTypeIconBar: React.FC<ProvisioningTemplateTypeIconBarProps> = ({
  onSelectTemplateType,
  onAddRoles,
  disabled = false,
}) => {
  const { t, currentLanguage } = useLanguage();
  const items = useMemo(
    () => provisioningTemplateTypeItems(t),
    [t, currentLanguage.id]
  );
  const legend = t('provisioningProfiles.addTemplateTypeBarLegend');
  const addRolesTitle = t('provisioningProfiles.addRole');
  const addRolesLabel = t('provisioningProfiles.rolesBarLabel');
  const toolbarAria = t('provisioningProfiles.ariaTemplateTypeToolbar');

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)]/40 p-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-secondary-text)] px-1 mb-2">
        {legend}
      </p>
      <div
        className="flex flex-nowrap gap-2 overflow-x-auto pb-1 scroll-smooth [scrollbar-width:thin]"
        role="toolbar"
        aria-label={toolbarAria}
      >
        {items.map(({ type, label, icon: Icon, description }) => (
          <button
            key={type}
            type="button"
            disabled={disabled}
            className={btnClass}
            title={description}
            aria-label={t('provisioningProfiles.ariaAddTemplate', { type: label })}
            onClick={() => onSelectTemplateType(type)}
          >
            <Icon className="h-6 w-6 text-[var(--color-info)] shrink-0 [stroke:currentColor]" />
            <span className="text-[10px] leading-tight text-center text-[var(--color-secondary-text)] line-clamp-2">
              {label}
            </span>
          </button>
        ))}
        <div className="w-px shrink-0 self-stretch bg-[var(--color-border)] mx-0.5" aria-hidden />
        <button
          type="button"
          disabled={disabled}
          className={btnClass}
          title={addRolesTitle}
          aria-label={addRolesTitle}
          onClick={onAddRoles}
        >
          <UserGroupIcon className="h-6 w-6 text-[var(--color-success)] shrink-0 [stroke:currentColor]" />
          <span className="text-[10px] leading-tight text-center text-[var(--color-secondary-text)] line-clamp-2">
            {addRolesLabel}
          </span>
        </button>
      </div>
    </div>
  );
};
